import { banco } from '../db/banco'
import { ufDoCep } from '../clientes/ufDoCep'
import { calcularImposto, type ComposicaoImposto } from './calcularImposto'
import { ICMS_INTERNO_POR_UF } from './icmsInternoPorUf'
import { icmsInterestadualPadrao } from './icmsInterestadualPadrao'

export interface EntradaResolverImposto {
  produtoId: string
  representadaId: string
  precoSemImposto: number
  cep: string | null
  freteTipo: 'CIF' | 'FOB' | 'REDESPACHO' | null
  descontoFretePct: number
  descontoPrazoPct: number
}

// Orquestra: busca representada, produto, aliquotas e a UF do destino, e so
// entao chama a formula pura. Qualquer dado faltando devolve null -- "nao
// calcula, vende como hoje" e sempre o retrocesso seguro (spec Sec.5).
export async function resolverImposto(
  entrada: EntradaResolverImposto,
): Promise<ComposicaoImposto | null> {
  const representada = await banco.representadas.get(entrada.representadaId)
  if (!representada?.calcula_imposto) return null
  const ufOrigem = representada.uf_origem ?? null
  if (!ufOrigem) return null

  const ufDestino = ufDoCep(entrada.cep)
  if (!ufDestino) return null
  // Venda dentro do mesmo estado: regime diferente ("reducao de ICMS"), fora
  // de escopo deste ciclo -- decisao 9 da spec. Cai no mesmo "nao calcula".
  if (ufDestino === ufOrigem) return null

  const produto = await banco.produtos.get(entrada.produtoId)
  if (!produto) return null

  // Pedido dela em 08/09/2026: sem linha importada para o produto, usa o
  // padrao interestadual do Confaz (so depende do par de estados) em vez de
  // desistir. A importada, quando existir, e sempre a mais especifica e
  // continua vencendo -- o padrao e so o fallback.
  const aliquotaIcms = await banco.aliquotas_icms
    .where('[produto_id+uf_destino]')
    .equals([entrada.produtoId, ufDestino])
    .filter((a) => a.apagado_em === null)
    .first()
  const icmsOrigemPct = aliquotaIcms?.aliquota_pct ?? icmsInterestadualPadrao(ufOrigem, ufDestino)

  const icmsInternoDestinoPct = ICMS_INTERNO_POR_UF[ufDestino]
  if (icmsInternoDestinoPct === undefined) return null

  const aliquotaSt = await banco.aliquotas_st
    .where('[produto_id+uf_destino]')
    .equals([entrada.produtoId, ufDestino])
    .filter((a) => a.apagado_em === null)
    .first()

  // Achado dela em 08/09: 170 produtos ja tem ST cadastrado na FICHA
  // (produtos.st_pct, Etapa 1 de campos fiscais), mas aliquotas_st (importacao
  // por produto+UF) estava vazia -- toda diferenca de ICMS saia zero mesmo em
  // produto com ST de verdade. Mesmo padrao do fallback de ICMS interestadual
  // acima: a linha importada, quando existe, sempre vence (e mais especifica,
  // por destino); a ficha do produto e so o padrao quando nao ha importacao.
  const mvaStPct = aliquotaSt?.mva_pct ?? produto.st_pct ?? 0

  return calcularImposto({
    precoSemImposto: entrada.precoSemImposto,
    icmsOrigemPct,
    icmsInternoDestinoPct,
    pisCofinsPct: produto.pis_cofins_pct ?? 0,
    ipiPct: produto.ipi_pct ?? 0,
    mvaStPct,
    descontoEntregaPct: entrada.descontoFretePct,
    descontoPrazoPct: entrada.descontoPrazoPct,
  })
}
